import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Card, Empty, Loading, Plate, Stat, formatDateTime, formatKm } from '../components/ui';

const heute = () => new Date().toISOString().slice(0, 10);

/**
 * Ersatz fuer das Papier-Fahrtenbuch: beantwortet auf einen Blick
 * "wer ist Fahrzeug X am Tag Y gefahren".
 */
export const PoolPage: React.FC = () => {
  const [datum, setDatum] = useState('');
  const [fahrer, setFahrer] = useState('');

  const { data: offen } = useQuery({
    queryKey: ['pool-offen'],
    queryFn: () => api<any[]>('/pool/offen'),
  });

  const params = new URLSearchParams({ pageSize: '100' });
  if (datum) params.set('date', datum);
  if (fahrer) params.set('driver', fahrer);

  const { data: fahrten, isLoading } = useQuery({
    queryKey: ['pool-fahrten', params.toString()],
    queryFn: () => api<{ total: number; items: any[] }>(`/pool/fahrten?${params}`),
  });

  return (
    <div className="grid" style={{ gap: 16 }}>
      <div className="grid k3">
        <Stat label="Aktuell unterwegs" value={offen?.length ?? 0} tone="accent" hint="Fahrzeuge ohne Abmeldung" />
        <Stat label="Fahrten (Filter)" value={fahrten?.total ?? 0} />
        <Stat
          label="Längste offene Fahrt"
          value={
            offen && offen.length > 0
              ? `${Math.round((Date.now() - new Date(offen[0].checkoutAt).getTime()) / 3600000)} h`
              : '—'
          }
          hint={offen?.[0]?.vehicle.plate ?? ''}
          tone={
            offen && offen.length > 0 && Date.now() - new Date(offen[0].checkoutAt).getTime() > 36e5 * 24
              ? 'warn-accent'
              : undefined
          }
        />
      </div>

      <Card title={`Aktuell ausgegeben (${offen?.length ?? 0})`} bodyless>
        {!offen || offen.length === 0 ? (
          <Empty icon="✓" text="Alle Poolfahrzeuge sind zurück" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Fahrzeug</th><th>Fahrer*in</th><th>Seit</th><th>Dauer</th><th className="num">km bei Ausgabe</th><th>Zweck</th><th>Beleg</th></tr>
              </thead>
              <tbody>
                {offen.map((t) => {
                  const stunden = Math.round((Date.now() - new Date(t.checkoutAt).getTime()) / 36e5);
                  return (
                    <tr key={t.id}>
                      <td>
                        <Link to={`/fahrzeuge/${t.vehicle.id}`}><Plate value={t.vehicle.plate} /></Link>
                        <div className="small muted">{[t.vehicle.make, t.vehicle.model].filter(Boolean).join(' ')}</div>
                      </td>
                      <td><b>{t.driverName}</b></td>
                      <td>{formatDateTime(t.checkoutAt)}</td>
                      <td>
                        <span className={`badge ${stunden > 24 ? 'warn' : 'blue'}`}>
                          {stunden < 24 ? `${stunden} h` : `${Math.round(stunden / 24)} Tage`}
                        </span>
                      </td>
                      <td className="num">{formatKm(t.mileageOut)}</td>
                      <td className="small">{t.purpose ?? '—'}</td>
                      <td>
                        {t.checkoutProtocol ? (
                          <Link className="mono small" to={`/protokolle/${t.checkoutProtocol.id}`}>
                            {t.checkoutProtocol.number}
                          </Link>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Fahrtenbuch-Ersatz – wer fuhr wann?">
        <div className="toolbar">
          <div className="field">
            <label>Stichtag</label>
            <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
          </div>
          <div className="field grow">
            <label>Fahrer*in</label>
            <input value={fahrer} onChange={(e) => setFahrer(e.target.value)} placeholder="Name …" />
          </div>
          <button className="btn" onClick={() => setDatum(heute())}>Heute</button>
          <button className="btn" onClick={() => { setDatum(''); setFahrer(''); }}>Filter zurücksetzen</button>
        </div>
      </Card>

      <Card title={fahrten ? `${fahrten.total} Fahrten` : 'Fahrten'} bodyless>
        {isLoading ? (
          <Loading />
        ) : !fahrten || fahrten.items.length === 0 ? (
          <Empty text="Keine Fahrten im gewählten Zeitraum" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Fahrzeug</th><th>Fahrer*in</th><th>Übernahme</th><th>Rückgabe</th>
                  <th className="num">gefahren</th><th>Zweck</th><th>Belege</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {fahrten.items.map((t) => (
                  <tr key={t.id}>
                    <td><Link to={`/fahrzeuge/${t.vehicle.id}`}><Plate value={t.vehicle.plate} /></Link></td>
                    <td><b>{t.driverName}</b></td>
                    <td>{formatDateTime(t.checkoutAt)}</td>
                    <td>{t.checkinAt ? formatDateTime(t.checkinAt) : <span className="badge warn">offen</span>}</td>
                    <td className="num">{t.gefahreneKm != null ? `${t.gefahreneKm} km` : '—'}</td>
                    <td className="small">{t.purpose ?? '—'}</td>
                    <td className="small">
                      {t.checkoutProtocol ? (
                        <Link className="mono" to={`/protokolle/${t.checkoutProtocol.id}`}>↑</Link>
                      ) : null}{' '}
                      {t.checkinProtocol ? (
                        <Link className="mono" to={`/protokolle/${t.checkinProtocol.id}`}>↓</Link>
                      ) : null}
                    </td>
                    <td>
                      <span className={`badge ${t.status === 'OFFEN' ? 'warn' : 'green'}`}>{t.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
